import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { FileText, Plus, Trash2, Save, ArrowLeft, RotateCcw } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { EmptyState } from "../components/Skeletons";
import SignaturePad from "../components/SignaturePad";

const DRAFT_KEY = "kretara_po_draft";

export default function PurchaseOrders() {
  const [approved, setApproved] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [items, setItems] = useState([]);
  const [lines, setLines] = useState([]);
  const [vendorId, setVendorId] = useState("");
  const [deptId, setDeptId] = useState("");
  const [filterBrand, setFilterBrand] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [transport, setTransport] = useState("Road");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [lastDispatch, setLastDispatch] = useState("");
  const [remarks, setRemarks] = useState("");
  const [signature, setSignature] = useState(null);
  const [nextPoNumber, setNextPoNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);

  const load = async () => {
    const [r, v, d, it, n] = await Promise.all([
      api.get("/requests", { params: { status: "approved" } }),
      api.get("/vendors"),
      api.get("/departments"),
      api.get("/items"),
      api.get("/purchase-orders/next-number"),
    ]);
    setApproved(r.data.items || []);
    setVendors(v.data || []);
    setDepartments(d.data || []);
    setItems(it.data || []);
    setNextPoNumber(n.data.next_po_number);
  };

  useEffect(() => { load(); }, []);

  // Restore draft on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d && (d.lines?.length > 0 || d.vendorId)) {
          setHasDraft(true);
        }
      }
    } catch {}
  }, []);

  const resetForm = () => {
    setLines([]); setVendorId(""); setDeptId("");
    setContactPerson(""); setTransport("Road");
    setDeliveryDate(""); setLastDispatch("");
    setRemarks(""); setSignature(null);
    setFilterBrand("");
  };

  const saveDraft = () => {
    if (lines.length === 0 && !vendorId) {
      toast.error("Nothing to save yet — pick a vendor or add a line first");
      return;
    }
    const draft = {
      lines, vendorId, deptId, contactPerson, transport,
      deliveryDate, lastDispatch, remarks, signature,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    setHasDraft(true);
    toast.success("Draft saved — you can return any time");
  };

  const loadDraft = () => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      setLines(d.lines || []);
      setVendorId(d.vendorId || "");
      setDeptId(d.deptId || "");
      setContactPerson(d.contactPerson || "");
      setTransport(d.transport || "Road");
      setDeliveryDate(d.deliveryDate || "");
      setLastDispatch(d.lastDispatch || "");
      setRemarks(d.remarks || "");
      setSignature(d.signature || null);
      toast.success("Draft loaded");
    } catch (e) {
      toast.error("Could not load draft");
    }
  };

  const discardDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setHasDraft(false);
    resetForm();
    toast.success("Draft discarded");
  };

  const onVendorChange = (id) => {
    setVendorId(id);
    const v = vendors.find((x) => x.id === id);
    setContactPerson(v?.contact_person || "");
  };

  const itemName = (id) => items.find((i) => i.id === id)?.name || "Item";

  const addLinesFromApproved = (r) => {
    const newLines = r.sizes.map((s) => ({
      request_id: r.id,
      item_name: itemName(r.item_id),
      size: s.size,
      quantity: s.quantity,
      rate: 0,
    }));
    setLines([...lines, ...newLines]);
    if (!vendorId && r.vendor_id) onVendorChange(r.vendor_id);
    if (!deptId && r.department_id) setDeptId(r.department_id);
  };

  const addBlankLine = () => {
    setLines([...lines, { request_id: null, item_name: "", size: "", quantity: 1, rate: 0 }]);
  };

  const updateLine = (idx, field, value) => {
    setLines(lines.map((l, i) => (i === idx ? { ...l, [field]: field === "quantity" || field === "rate" ? Number(value) || 0 : value } : l)));
  };

  const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));

  const total = lines.reduce((a, l) => a + l.quantity * l.rate, 0);

  const filtered = approved.filter((r) => {
    if (filterBrand && !(r.brand || "").toLowerCase().includes(filterBrand.toLowerCase())) return false;
    if (deptId && r.department_id !== deptId) return false;
    return !lines.some((l) => l.request_id === r.id);
  });

  const generate = async () => {
    if (!vendorId || lines.length === 0) {
      toast.error("Pick a vendor and add at least one line item");
      return;
    }
    if (lines.some((l) => !l.item_name || l.quantity <= 0 || l.rate <= 0)) {
      toast.error("All lines need item, qty > 0 and rate > 0");
      return;
    }
    setSaving(true);
    try {
      const body = {
        vendor_id: vendorId,
        department_id: deptId || null,
        contact_person: contactPerson,
        transport_mode: transport,
        delivery_date: deliveryDate,
        last_dispatch_date: lastDispatch,
        remarks,
        signature,
        lines,
      };
      const { data } = await api.post("/purchase-orders", body);
      toast.success(`PO ${data.po_number} created`);
      // download PDF
      const pdf = await api.get(`/purchase-orders/${data.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(pdf.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data.po_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);

      // reset
      resetForm();
      localStorage.removeItem(DRAFT_KEY);
      setHasDraft(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }} data-testid="po-title">
            Generate purchase order
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Pick approved items, set rates, and create a PDF PO.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={saveDraft} className="gap-2" data-testid="po-save-draft">
            <Save size={14} /> Save draft
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" className="gap-2 text-muted-foreground" data-testid="po-reset">
                <RotateCcw size={14} /> Reset
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear the current PO?</AlertDialogTitle>
                <AlertDialogDescription>
                  All line items, vendor, and signature will be cleared. Saved drafts are not affected.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={resetForm} className="bg-red-600 hover:bg-red-700" data-testid="po-reset-confirm">
                  Yes, clear
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <div className="k-glass-strong rounded-xl px-4 py-2" data-testid="next-po-number">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Next PO #</div>
            <div className="text-lg font-bold" style={{ fontFamily: "Outfit", color: "#534AB7" }}>{nextPoNumber}</div>
          </div>
        </div>
      </div>

      {hasDraft && lines.length === 0 && !vendorId && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="k-glass rounded-2xl p-3 flex items-center gap-3 border-l-4 border-l-[#534AB7]"
          data-testid="draft-banner"
        >
          <Save size={16} className="text-[#534AB7]" />
          <div className="text-sm flex-1">You have a saved PO draft. Resume where you left off?</div>
          <Button size="sm" variant="ghost" onClick={discardDraft} data-testid="discard-draft">Discard</Button>
          <Button size="sm" className="bg-[#534AB7] hover:bg-[#463E9F]" onClick={loadDraft} data-testid="resume-draft">
            <ArrowLeft size={14} className="mr-1" /> Resume draft
          </Button>
        </motion.div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Filters */}
          <div className="k-glass rounded-2xl p-4 flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[180px]">
              <Label className="text-xs">Filter by brand</Label>
              <Input value={filterBrand} onChange={(e) => setFilterBrand(e.target.value)} placeholder="Brand..." data-testid="po-filter-brand" />
            </div>
            <div className="min-w-[160px]">
              <Label className="text-xs">Department</Label>
              <Select value={deptId} onValueChange={setDeptId}>
                <SelectTrigger data-testid="po-filter-dept"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={addBlankLine} className="gap-2" data-testid="add-blank-line">
              <Plus size={14} /> Add custom line
            </Button>
          </div>

          {/* Approved pool */}
          <div className="k-glass rounded-2xl p-4">
            <h3 className="font-semibold mb-3">Approved requests</h3>
            {filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">All approved items already added — or none available.</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                {filtered.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => addLinesFromApproved(r)}
                    className="text-left p-3 rounded-xl border border-border hover:bg-foreground/5 transition"
                    data-testid={`pool-${r.id}`}
                  >
                    <div className="text-sm font-medium">{r.brand} · {r.design}</div>
                    <div className="text-xs text-muted-foreground">{r.total_qty} units across {r.sizes.length} sizes</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* PO line items */}
          <div className="k-glass rounded-2xl p-4">
            <h3 className="font-semibold mb-3">Line items</h3>
            {lines.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Add approved items or a custom line above.</p>
            ) : (
              <div className="space-y-2">
                <div className="hidden sm:grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-muted-foreground px-2">
                  <div className="col-span-5">Item</div>
                  <div className="col-span-2">Size</div>
                  <div className="col-span-1">Qty</div>
                  <div className="col-span-2">Rate</div>
                  <div className="col-span-1">Amount</div>
                  <div className="col-span-1"></div>
                </div>
                {lines.map((l, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`po-line-${i}`}>
                    <Input className="col-span-5" value={l.item_name} onChange={(e) => updateLine(i, "item_name", e.target.value)} placeholder="Item name" />
                    <Input className="col-span-2" value={l.size || ""} onChange={(e) => updateLine(i, "size", e.target.value)} placeholder="Size" />
                    <Input className="col-span-1" type="number" value={l.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)} />
                    <Input className="col-span-2" type="number" value={l.rate} onChange={(e) => updateLine(i, "rate", e.target.value)} />
                    <div className="col-span-1 text-sm font-medium tabular-nums">₹{(l.quantity * l.rate).toFixed(0)}</div>
                    <button onClick={() => removeLine(i)} className="col-span-1 p-2 text-muted-foreground hover:text-destructive" data-testid={`remove-line-${i}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <div className="flex justify-end pt-3 border-t border-border/60 mt-3">
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">Total</div>
                    <div className="text-2xl font-bold" style={{ fontFamily: "Outfit", color: "#534AB7" }} data-testid="po-total">
                      ₹{total.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right column: vendor + meta + signature */}
        <div className="space-y-6">
          <div className="k-glass rounded-2xl p-4 space-y-3">
            <h3 className="font-semibold">Vendor & delivery</h3>
            <div>
              <Label className="text-xs">Vendor</Label>
              <Select value={vendorId} onValueChange={onVendorChange}>
                <SelectTrigger data-testid="po-vendor"><SelectValue placeholder="Choose vendor" /></SelectTrigger>
                <SelectContent>
                  {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {vendorId && (
                <p className="text-xs text-muted-foreground mt-1">
                  {vendors.find((v) => v.id === vendorId)?.email}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs">Contact person</Label>
              <Input value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} data-testid="po-contact" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Last dispatch</Label>
                <Input type="date" value={lastDispatch} onChange={(e) => setLastDispatch(e.target.value)} data-testid="po-last-dispatch" />
              </div>
              <div>
                <Label className="text-xs">Delivery</Label>
                <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} data-testid="po-delivery" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Transport</Label>
              <Select value={transport} onValueChange={setTransport}>
                <SelectTrigger data-testid="po-transport"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Road", "Rail", "Air", "Sea"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Remarks</Label>
              <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="po-remarks" />
            </div>
          </div>

          <div className="k-glass rounded-2xl p-4">
            <h3 className="font-semibold mb-3">Signature</h3>
            <SignaturePad value={signature} onChange={setSignature} />
          </div>

          <motion.div whileHover={{ y: -2 }}>
            <Button
              onClick={generate}
              disabled={saving || lines.length === 0 || !vendorId}
              className="w-full bg-[#534AB7] hover:bg-[#463E9F] gap-2 h-12"
              data-testid="generate-po"
            >
              <FileText size={16} />
              {saving ? "Generating..." : "Generate PO & download PDF"}
            </Button>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
